import { isNil } from 'lodash-es';

import { NavigationSection } from '@src/components/Navigation';
import type { AppPage, FeatureFlags, OrgRoutes } from '@src/types';

import { isAuthorisedPathForUser } from '../pages/auth/isAuthorisedPathForUser';

/**
 * The possible navigation sections to show in the site navigation.
 * They will be shown in this order.
 */
const navigationSections = [
  { id: 'boost', label: 'Boost' },
  { id: 'priceOptimisation', label: 'Price Optimisation' },
  { id: 'customerIntelligence', label: 'Customer Intelligence' },
] as const;

/**
 * Generates the navigation sections configuration from the routes the user is authorised
 * to view and the available page links to show in the navigation.
 */
export function getNavigationSections(
  orgAuthorisedRoutes: OrgRoutes | null,
  appPages: readonly AppPage[],
  orgName: string | null,
  region: string | null,
  featureFlags: FeatureFlags,
): NavigationSection[] {
  const result: NavigationSection[] = [];
  const regionalOrgPath = `/${region}/${orgName}`;

  if (!orgAuthorisedRoutes) {
    return result;
  }

  // biome-ignore lint/complexity/noForEach: TODO: fix
  navigationSections.forEach((navigationSection) => {
    const links = appPages.filter(
      ({ navigationConfig, path }) =>
        navigationConfig &&
        navigationConfig.sectionId === navigationSection.id &&
        isAuthorisedPathForUser(orgAuthorisedRoutes, navigationConfig.pathOverride ?? path) &&
        (isNil(navigationConfig.isEnabled) || navigationConfig.isEnabled(featureFlags)),
    );
    if (links.length) {
      result.push({
        sectionLabel: navigationSection.label,
        links: links.map((link) => ({
          label: link.navigationConfig?.label ?? '',
          // biome-ignore lint/style/noNonNullAssertion: TODO: fix
          icon: link.navigationConfig?.icon!,
          to: `${regionalOrgPath}${link.navigationConfig?.pathOverride ?? link.path}`,
          disabled: orgAuthorisedRoutes?.find((r) => r.route === link.path)?.disabled,
        })),
      });
    }
  });

  return result;
}
